import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { NomaMark } from '@/components/generals/noma-mark'

const WORDMARK = Array.from('STRENGTH')

/* Runs once per browser session. Rendered on the server so the first paint is
   already the black curtain, which is what stops the hero flashing in behind
   it. The session check happens after mount, so the initial state stays a
   constant and hydration matches. */
export const Preloader: FC = (): ReactNode => {
    const scope = useRef<HTMLDivElement>(null)
    const counterRef = useRef<HTMLSpanElement>(null)
    const [done, setDone] = useState(false)

    useGsap(() => {
        const root = scope.current

        if (!root) {
            return
        }

        const seen = window.sessionStorage.getItem('noma-intro') === 'seen'

        const finish = (): void => {
            window.sessionStorage.setItem('noma-intro', 'seen')
            document.documentElement.removeAttribute('data-loading')
            window.dispatchEvent(new CustomEvent('noma:intro-done'))
            setDone(true)
        }

        if (seen || prefersReducedMotion()) {
            gsap.set(root, { autoAlpha: 0 })
            finish()

            return
        }

        document.documentElement.setAttribute('data-loading', 'true')

        const counter = { value: 0 }
        const tl = gsap.timeline({ onComplete: finish })

        tl.to('[data-intro-letter]', {
            yPercent: 0,
            opacity: 1,
            duration: 0.9,
            stagger: 0.07,
            ease: 'noma'
        }, 0)

        tl.to('[data-intro-mark]', {
            scale: 1,
            opacity: 1,
            rotate: 0,
            duration: 1,
            ease: 'noma'
        }, 0.1)

        tl.to('[data-intro-rule]', {
            scaleX: 1,
            duration: 1.5,
            ease: 'noma'
        }, 0.1)

        tl.to(counter, {
            value: 100,
            duration: 1.5,
            ease: 'power2.inOut',
            onUpdate: () => {
                if (counterRef.current) {
                    counterRef.current.textContent = String(Math.round(counter.value)).padStart(3, '0')
                }
            }
        }, 0.1)

        tl.to('[data-intro-meta]', {
            opacity: 1,
            duration: 0.6
        }, 0.3)

        /* Curtain leaves as two panels wiping upward, the letters riding out
           slightly ahead of them so the hero is already moving underneath. */
        tl.to('[data-intro-letter], [data-intro-mark]', {
            yPercent: -120,
            opacity: 0,
            duration: 0.8,
            stagger: 0.03,
            ease: 'power3.in'
        }, 1.75)

        tl.to('[data-intro-meta], [data-intro-rule]', {
            opacity: 0,
            duration: 0.4
        }, 1.75)

        tl.to('[data-intro-panel]', {
            scaleY: 0,
            duration: 1.05,
            stagger: 0.06,
            ease: 'noma'
        }, 2.05)

        return () => {
            document.documentElement.removeAttribute('data-loading')
        }
    }, scope, [])

    if (done) {
        return null
    }

    return (
        <div
            ref={scope}
            className='fixed inset-0 z-[120] flex items-center justify-center'
            aria-hidden='true'
        >
            <div className='absolute inset-0 flex'>
                {[0, 1, 2, 3, 4].map((panel) => (
                    <span
                        key={panel}
                        data-intro-panel
                        className='h-full flex-1 origin-top bg-ink'
                    />
                ))}
            </div>

            <div className='relative flex flex-col items-center gap-5 px-6'>
                <div className='flex items-end gap-[0.02em] overflow-hidden pb-[0.12em]'>
                    <span
                        data-intro-mark
                        className='mb-[0.18em] inline-flex text-brand opacity-0'
                        style={{ transform: 'scale(0.4) rotate(-25deg)' }}
                    >
                        <NomaMark className='size-[clamp(18px,3vw,34px)]' />
                    </span>

                    {WORDMARK.map((letter, index) => (
                        <span
                            key={`${letter}-${index}`}
                            data-intro-letter
                            className='t-display inline-block text-[clamp(40px,9vw,130px)] leading-[0.8] text-paper opacity-0'
                            style={{ transform: 'translateY(110%)' }}
                        >
                            {letter}
                        </span>
                    ))}
                </div>

                <span
                    data-intro-rule
                    className='block h-px w-[min(60vw,420px)] origin-left bg-paper/25'
                    style={{ transform: 'scaleX(0)' }}
                />

                <div
                    data-intro-meta
                    className='flex w-[min(60vw,420px)] items-center justify-between opacity-0'
                >
                    <span className='t-mono text-ash-soft'>
                        Brand &amp; Web Studio
                    </span>

                    <span className='t-mono text-brand'>
                        <span ref={counterRef}>
                            000
                        </span>
                        <span>
                            %
                        </span>
                    </span>
                </div>
            </div>
        </div>
    )
}
